// Rejestr akcji „jak w Excelu” (SR-008 + SR-014): edycja w komórkach, autozapis wiersza,
// wklejanie wielu wierszy z Excela (Ctrl+V) z podglądem. Kolumny jak w arkuszu klienta.
// Tabela jest wspólna z rejestrem ośrodka (IssueLogGrid); tu tylko źródło danych: akcje raportu.
import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { addReportAction, deleteReportAction, downloadActionLogTemplate, parseActionLogSpreadsheet, updateReportAction } from "../../../api/reportsApi";
import { ActionStatus, ReportActionDto } from "../../../types/reportActions";
import { DiscussionHistoryInline } from "../../../components/atomic/discussionComponent";
import { ActionLogRow, REPORT_ACTION_RULES } from "./actionLogFormat";
import { IssueLogGrid, IssueLogSource } from "./IssueLogGrid";

const fromDto = (a: ReportActionDto): ActionLogRow => ({
  key: a.id,
  id: a.id,
  participantNumber: a.participantNumber ?? "",
  visitNumber: a.visitNumber ?? "",
  categoryId: a.categoryId ?? null,
  deviationDescription: a.deviationDescription ?? "",
  identificationDate: a.identificationDate ? a.identificationDate.split("T")[0] : null,
  closedDate: a.closedDate ? a.closedDate.split("T")[0] : null,
  status: a.status ?? ActionStatus.Open,
  actionsTaken: a.actionsTaken ?? "",
  responsiblePerson: a.responsiblePerson ?? "",
  potentialDeviation: !!a.potentialDeviation,
  potentialSeriousFinding: !!a.potentialSeriousFinding,
  seriousBreach: !!a.seriousBreach,
});

const toPayload = (r: ActionLogRow): Partial<ReportActionDto> => ({
  participantNumber: r.participantNumber.trim(),
  visitNumber: r.visitNumber.trim() || null,
  categoryId: r.categoryId,
  deviationDescription: r.deviationDescription.trim(),
  identificationDate: r.identificationDate ?? undefined,
  closedDate: r.status === ActionStatus.Closed ? r.closedDate ?? undefined : undefined,
  status: r.status,
  actionsTaken: r.actionsTaken.trim() || undefined,
  responsiblePerson: r.responsiblePerson.trim() || null,
  potentialDeviation: r.potentialDeviation,
  potentialSeriousFinding: r.potentialSeriousFinding,
  seriousBreach: r.seriousBreach,
  extendedFields: true,
});

export const ActionLogGrid = ({
  reportId,
  actions,
  isLoading,
  error,
}: {
  reportId: string;
  actions: ReportActionDto[] | undefined;
  isLoading: boolean;
  error: any;
}) => {
  const queryClient = useQueryClient();

  const rows = useMemo(() => actions
    ?.slice()
    .sort((a, b) => (a.identificationDate || "").localeCompare(b.identificationDate || "") || (a.created || "").localeCompare(b.created || ""))
    .map(fromDto), [actions]);

  const source: IssueLogSource = {
    rows,
    isLoading,
    error,
    save: async (row) => {
      if (row.id) {
        await updateReportAction(reportId, row.id, { ...toPayload(row), id: row.id, reportId });
        return row.id;
      }
      const res: any = await addReportAction(reportId, toPayload(row));
      return typeof res === "string" ? res : res?.id;
    },
    remove: (id) => deleteReportAction(reportId, id),
    refresh: () => queryClient.invalidateQueries([reportId, "ReportActions"]),
    parseXlsx: (file) => parseActionLogSpreadsheet(reportId, file),
    downloadTemplate: () => downloadActionLogTemplate(reportId, false),
    downloadData: () => downloadActionLogTemplate(reportId, true),
  };

  return (
    <IssueLogGrid
      source={source}
      title="Akcje otwarte i zamknięte"
      rules={REPORT_ACTION_RULES}
      dataExport={{ label: "Rejestr tego raportu (.xlsx)", hint: "obecne pozycje do pracy w Excelu", fileName: "Rejestr_akcji" }}
      renderRowActions={(row) => <DiscussionHistoryInline resourceId={row.id!} />}
    />
  );
};
